import {INVE_FIELDS} from '@/constants/inve';
import {PRV_COMP_ARRAY} from '@/constants/prv-comp';
import {and, not, or} from '@/model/helpers/conditions/utils';
import {Validations} from '@/model/helpers/validations';
import {Contributor} from '@/model/src/values/dv-11';
import {ParticipantOrExSpouse} from '@/model/src/values/dv-12';
import {YesNoChoice} from '@/model/src/values/dv-3';
import {T2033Requirement} from '@/model/src/values/dv-42';
import {SourcePlans} from '@/model/src/values/dv-71';
import {Plans} from '@/model/src/values/dv-9';
import {Booleans} from '@/model/src/values/dv-boolean';
import {Condition, Event, Reference, SupportsSwitch, SwitchCases, Validation} from '@/model/types';
import {ALL_I_ARRAYS, I_Array, I_ARRAYS} from '../src/sections/section-i/constants';
import {DPAType} from '../src/values/dv-40';
import {ContractTypes} from '../src/values/dv-93';
import {ConversionType} from '../src/values/dv-97';
import {clearEntries} from './actions/array';
import {batch, unset} from './actions/fields';
import {Conditions} from './conditions';
import {
  isContractholder,
  isContractType,
  isExSpouse,
  isFRV_FRR_FRVR,
  isParticipant,
  isPlan,
  isSpousal,
} from './section-c';
import {isExistingAccountsContractType} from './section-i-is-existing-accounts-contract-type';
import {isPrefilledClient} from './section-inve';
import {buildSourcePlanRules, SourcePlanRule, SourcePlanRuleFields} from './source-plan-rules';
import {template} from './template';

export {isExistingAccountsContractType as isSelectedExistingAccountsContractType} from './section-i-is-existing-accounts-contract-type';

const MANUAL_SOURCE_PLAN_RULE_FIELDS: SourcePlanRuleFields = {
  isPlan,
  isSourceAccountContractType: isContractType,
  isSourceAccountContractTypeForFHSACondition: isExistingAccountsContractType,
  isContractholder,
  isSpousal,
  isExSpouse,
  isParticipant,
  mode: 'manual',
};

export const SOURCE_PLAN_RULES: SourcePlanRule[] = buildSourcePlanRules(MANUAL_SOURCE_PLAN_RULE_FIELDS);

export const PREFILLED_ACCOUNT_SOURCE_PLAN_RULES: SourcePlanRule[] = buildSourcePlanRules({
  ...MANUAL_SOURCE_PLAN_RULE_FIELDS,
  isSourceAccountContractTypeForFHSACondition: (type: ContractTypes): Condition =>
    Conditions.fields.eq(INVE_FIELDS.INVE44_existingAccountProductType.asIndex(), type),
});

const SOURCE_ACCOUNT_PLAN_FIELD = template('${INVE45#${I211}}');
const SOURCE_ACCOUNT_SPOUSAL_FLAG_FIELD = template('${INVE40#${I211}}');
const SOURCE_ACCOUNT_PURCHASER_MEMBER_FIELD = template('${INVE47#${I211}}');

const isSourceAccountContractholder = Conditions.fields.eq(
  SOURCE_ACCOUNT_SPOUSAL_FLAG_FIELD,
  Contributor.CONTRACTHOLDER,
);
const isSourceAccountSpousal = Conditions.fields.eq(SOURCE_ACCOUNT_SPOUSAL_FLAG_FIELD, Contributor.SPOUSAL);
const isSourceAccountExSpouse = Conditions.fields.eq(
  SOURCE_ACCOUNT_PURCHASER_MEMBER_FIELD,
  ParticipantOrExSpouse.EX_SPOUSE,
);
const isSourceAccountParticipant = Conditions.fields.eq(
  SOURCE_ACCOUNT_PURCHASER_MEMBER_FIELD,
  ParticipantOrExSpouse.PARTICIPANT,
);

const PLAN_TO_SOURCE_PLAN_MAPPING: {plan: Plans; sourcePlan: SourcePlans; role?: Condition}[] = [
  {plan: Plans.NRSP, sourcePlan: SourcePlans.NRSP_Non_Registered},
  {plan: Plans.TFSA, sourcePlan: SourcePlans.TFSA},
  {plan: Plans.FHSA, sourcePlan: SourcePlans.FHSA},

  {plan: Plans.RSP, sourcePlan: SourcePlans.RRSP_Contributing_Contractholder, role: isSourceAccountContractholder},
  {plan: Plans.RSP, sourcePlan: SourcePlans.RRSP_Contributing_Spouse, role: isSourceAccountSpousal},
  {plan: Plans.RIF, sourcePlan: SourcePlans.RRIF_Contributing_Contractholder, role: isSourceAccountContractholder},
  {plan: Plans.RIF, sourcePlan: SourcePlans.RRIF_Contributing_Spouse, role: isSourceAccountSpousal},

  {plan: Plans.LIRA, sourcePlan: SourcePlans.LIRA_Purchaser_Contractholder, role: isSourceAccountExSpouse},
  {plan: Plans.LIRA, sourcePlan: SourcePlans.LIRA_Member_Contractholder, role: isSourceAccountParticipant},
  {plan: Plans.LIF, sourcePlan: SourcePlans.LIF_Purchaser_Contractholder, role: isSourceAccountExSpouse},
  {plan: Plans.LIF, sourcePlan: SourcePlans.LIF_Member_Contractholder, role: isSourceAccountParticipant},
  {plan: Plans.RLIF, sourcePlan: SourcePlans.RLIF_Purchaser_Contractholder, role: isSourceAccountExSpouse},
  {plan: Plans.RLIF, sourcePlan: SourcePlans.RLIF_Member_Contractholder, role: isSourceAccountParticipant},
  {plan: Plans.RLSP, sourcePlan: SourcePlans.RLSP_Purchaser_Contractholder, role: isSourceAccountExSpouse},
  {plan: Plans.RLSP, sourcePlan: SourcePlans.RLSP_Member_Contractholder, role: isSourceAccountParticipant},
];

export const planToSourcePlanCases = (): SwitchCases<Reference> =>
  PLAN_TO_SOURCE_PLAN_MAPPING.map(({plan, sourcePlan, role}) => ({
    $case: [
      Conditions.fields.isFilled('I211'),
      Conditions.fields.eq(SOURCE_ACCOUNT_PLAN_FIELD, {$ref: plan}),
      ...(role ? [role] : []),
    ],
    $then: {$ref: sourcePlan},
  }));

export const setBankingFieldsOnBankAccountSelectionEvents = [
  {action: {type: 'set', field: 'I181', value: {$template: '${INVE34#${I177}}'}}},
  {action: {type: 'set', field: 'I180', value: {$template: '${INVE35#${I177}}'}}},
  {action: {type: 'set', field: 'I182', value: {$template: '${INVE36#${I177}}'}}},
] satisfies Event[];

export const hasEnabledExistingAccounts = Conditions.fields.eq('I10', Booleans.True);

export const hasSelectedExistingAccountsContractType = Conditions.fields.isFilled('I193');

export const hasSelectedSourcePlanOrIsNotPrefilled = or(Conditions.fields.isFilled('I211'), not(isPrefilledClient));

export const canSelectExistingAccountFromPrefilledInvestor = and(
  isPrefilledClient,
  hasEnabledExistingAccounts,
  hasSelectedExistingAccountsContractType,
);

export const isSameContractTypeTransfer = Conditions.fields.eq('I193', {$ref: 'C14'});

export const isCrossContractTypeTransfer = not(isSameContractTypeTransfer);

export const hasEnabledExternalFundTransfer1 = Conditions.fields.eq('I20', Booleans.True);

export const shouldGenerateT2033ForExternalFundTransfer1 = Conditions.fields.eq('I48', T2033Requirement.YES);

export const shouldAddT2033ForExternalFundTransfer1 = Conditions.fields.eq('I48', T2033Requirement.NO_OTHER_FORM);

export const isBeneficiarySignatureRequiredForExternalFundTransfer1 = Conditions.fields.eq('I60', YesNoChoice.YES);

export const hasEnabledExternalFundTransfer2 = Conditions.fields.eq('I29', Booleans.True);

export const shouldGenerateT2033ForExternalFundTransfer2 = Conditions.fields.eq('I62', T2033Requirement.YES);

export const shouldAddT2033ForExternalFundTransfer2 = Conditions.fields.eq('I62', T2033Requirement.NO_OTHER_FORM);

export const isBeneficiarySignatureRequiredForExternalFundTransfer2 = Conditions.fields.eq('I74', YesNoChoice.YES);

export const hasEnabledExternalFundTransfer3 = Conditions.fields.eq('I38', Booleans.True);

export const shouldGenerateT2033ForExternalFundTransfer3 = Conditions.fields.eq('I76', T2033Requirement.YES);

export const shouldAddT2033ForExternalFundTransfer3 = Conditions.fields.eq('I76', T2033Requirement.NO_OTHER_FORM);

export const isBeneficiarySignatureRequiredForExternalFundTransfer3 = Conditions.fields.eq('I88', YesNoChoice.YES);

export const hasEnabledExternalFundTransfer4 = Conditions.fields.eq('I107', Booleans.True);

export const shouldGenerateT2033ForExternalFundTransfer4 = Conditions.fields.eq('I108', T2033Requirement.YES);

export const shouldAddT2033ForExternalFundTransfer4 = Conditions.fields.eq('I108', T2033Requirement.NO_OTHER_FORM);

export const isBeneficiarySignatureRequiredForExternalFundTransfer4 = Conditions.fields.eq('I130', YesNoChoice.YES);

export const hasNotSignedRightToReimbursement = Conditions.fields.eq('I104', YesNoChoice.NO);

export const takeAmountFromDifferentBankAccount = Conditions.fields.eq('I105', YesNoChoice.NO);

export const hasEnabledLoan = Conditions.fields.eq('I16', Booleans.True);

export const hasEnabledPayrollDeduction = Conditions.fields.eq('I167', Booleans.True);

export const hasEnabledPreauthorizedDebit = Conditions.fields.eq('I1', Booleans.True);

export const isDPAType = <T extends DPAType>(value: T) => {
  return Conditions.fields.eq('I47', value);
};

export const isDPATypeIn = (...value: DPAType[]) => {
  return Conditions.fields.isIn('I47', value);
};

export const isPartialConversionOfPlanOrHidden = or(
  Conditions.fields.eq('I92', ConversionType.PARTIAL),
  Conditions.fields.isHidden('I92'),
);

export const isNotTotalConversionOfPlan = Conditions.fields.ne('I92', ConversionType.TOTAL);

export const isSameGuarantee = Conditions.fields.eq('I162', {$ref: 'C13'});

export const hasMaxAccountOfAnyType = <TValue extends number>(max: TValue) => {
  return Conditions.array.for(...ALL_I_ARRAYS).rowCount.gte(max, false);
};

export const hasMaxOfAccountType = <TArray extends I_Array, TValue extends number>(array: TArray, max: TValue) => {
  return Conditions.array.for(array).rowCount.gte(max);
};

export const hasAnyOfAccountType = <TArray extends I_Array>(array: TArray) => {
  return Conditions.array.for(array).rowCount.gt(0);
};

export const isSourcePlanIn = <TSourcePlan extends SourcePlans>(...sourcePlans: TSourcePlan[]) =>
  Conditions.fields.isIn('I100', sourcePlans);

export const clearIAccountArrays = clearEntries([...ALL_I_ARRAYS]);

export const clearSourcePlanSelection = batch([unset('I211'), clearIAccountArrays]);

export const MAX_ACCOUNTS_PER_TYPE_I: Record<I_Array, number> = {
  [I_ARRAYS.I_dailyInterestAccounts]: 1,
  [I_ARRAYS.I_highYieldAccounts]: 1,
  [I_ARRAYS.I_guaranteedInterestAccounts]: 5,
  [I_ARRAYS.I_benevaInvestmentAccounts]: 5,
  [I_ARRAYS.I_benevaGuaranteedInvestmentFunds]: 5,
};

export const hasReachedMaxOfAccountType = (array: I_Array): Condition => {
  return Conditions.utils.or(
    Conditions.utils.and(isFRV_FRR_FRVR, hasMaxAccountOfAnyType(5)),
    hasMaxOfAccountType(array, MAX_ACCOUNTS_PER_TYPE_I[array]),
  );
};

export const hasAnyOfSourceAccountType = (array: PRV_COMP_ARRAY): Condition => {
  return Conditions.array.for(array).rowCount.gt(0, false);
};

export const buildAmountValidation = (
  amountField: string,
  prvCompBalanceField: string,
  isPrefilledAccountCondition: Condition,
): SupportsSwitch<Validation[]> => ({
  $switch: [
    {
      $case: [isPrefilledAccountCondition],
      $then: [
        Validations.condition(
          Conditions.fields.isLessThanOrEqual(amountField, {
            $ref: prvCompBalanceField,
          }),
          {
            fr: 'Le montant saisi est plus élevé que le solde disponible.',
            en: 'The amount entered is greater than the available balance.',
          },
        ),
        ...Validations.number.validAmount,
      ],
    },
    {
      $default: [...Validations.number.validAmount],
    },
  ],
});

const hasExternalFundTransfersConditions = [
  hasEnabledExternalFundTransfer1, // I20
  hasEnabledExternalFundTransfer2, // I29
  hasEnabledExternalFundTransfer3, // I38
  hasEnabledExternalFundTransfer4, // I107
] satisfies Condition[];

export const hasEnabledExternalFundTransfers = or(...hasExternalFundTransfersConditions) satisfies Condition;

// True when at least one external provenance is enabled in Section I.
// Per functional spec (Onglet C): deposit with A16=DV-48#0 AND all of I10, I16, I20, I29, I38, I107 = false
// means DPA (I1) and/or retenue salariale (I167) are the only active provenances → use IND036/FRA1394.
export const hasAnyExternalFundingSource = or(
  hasEnabledExistingAccounts, // I10
  hasEnabledLoan, // I16
  ...hasExternalFundTransfersConditions,
) satisfies Condition;

export const hasLoanOrExternalFundTransfer = or(
  hasEnabledLoan, // I16
  ...hasExternalFundTransfersConditions,
) satisfies Condition;

export const hasNoPayrollDeductionOrExternalTransferOrAccountFunding = not(
  or(
    hasEnabledPayrollDeduction, // I167
    ...hasExternalFundTransfersConditions,
    hasEnabledExistingAccounts, // I10
  ),
) satisfies Condition;
